export function initPartnersCarousel(trackEl, options) {
  if (!trackEl) return () => {};

  const direction = options?.direction === 'right' ? 'right' : 'left';
  const speedSeconds = Number.isFinite(options?.speedSeconds) ? options.speedSeconds : 12;

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion) return () => {};

  let rafId = 0;
  let lastTs = 0;
  let offset = 0;
  let cachedGap = 0;
  let containerWidth = 0;

  const ensureFill = () => {
    const container = trackEl.parentElement;
    if (!container) return;

    containerWidth = container.getBoundingClientRect().width;
    if (!Number.isFinite(containerWidth) || containerWidth <= 0) return;

    // Duplicate children until the track is wider than the viewport,
    // so there is no empty area on the right.
    const MAX_CLONES = 40;
    let clones = 0;

    while (trackEl.scrollWidth < containerWidth * 2 && clones < MAX_CLONES) {
      const children = Array.from(trackEl.children);
      if (!children.length) break;
      for (const child of children) {
        trackEl.appendChild(child.cloneNode(true));
        clones += 1;
        if (trackEl.scrollWidth >= containerWidth * 2 || clones >= MAX_CLONES) break;
      }
    }
  };

  const getGapPx = () => {
    const styles = window.getComputedStyle(trackEl);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    return Number.isFinite(gap) ? gap : 0;
  };

  const getItemStepPx = (el) => {
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return rect.width + cachedGap;
  };

  const measure = () => {
    cachedGap = getGapPx();
    ensureFill();
  };

  const rotateLeft = () => {
    const first = trackEl.firstElementChild;
    if (first) trackEl.appendChild(first);
  };

  const rotateRight = () => {
    const last = trackEl.lastElementChild;
    if (last) trackEl.insertBefore(last, trackEl.firstElementChild);
  };

  const step = (ts) => {
    if (!lastTs) lastTs = ts;
    const dt = ts - lastTs;
    lastTs = ts;

    if (cachedGap === 0) measure();

    const first = trackEl.firstElementChild;
    const last = trackEl.lastElementChild;

    // Use a constant, viewport-based speed so motion doesn't change based on logo widths.
    const pixelsPerMs = containerWidth / (Math.max(speedSeconds, 1) * 1000);

    if (pixelsPerMs > 0) {
      const dir = direction === 'left' ? -1 : 1;
      offset = offset + dir * pixelsPerMs * dt;

      if (direction === 'left') {
        let firstStep = getItemStepPx(first);
        while (firstStep > 0 && offset <= -firstStep) {
          offset = offset + firstStep;
          rotateLeft();
          firstStep = getItemStepPx(trackEl.firstElementChild);
        }
      }

      if (direction === 'right') {
        let lastStep = getItemStepPx(last);
        while (lastStep > 0 && offset >= lastStep) {
          offset = offset - lastStep;
          rotateRight();
          lastStep = getItemStepPx(trackEl.lastElementChild);
        }
      }

      trackEl.style.transform = `translate3d(${offset}px, 0, 0)`;
    }

    rafId = window.requestAnimationFrame(step);
  };

  const ro = new ResizeObserver(() => {
    measure();
  });
  ro.observe(trackEl);

  const imgs = Array.from(trackEl.querySelectorAll('img'));
  const onImgLoad = () => measure();
  imgs.forEach((img) => img.addEventListener('load', onImgLoad));

  measure();
  rafId = window.requestAnimationFrame(step);

  return () => {
    window.cancelAnimationFrame(rafId);
    ro.disconnect();
    imgs.forEach((img) => img.removeEventListener('load', onImgLoad));
    trackEl.style.transform = '';
  };
}
